import { useMemo, useState } from "react";
import { useRouter } from "expo-router";
import { StyleSheet, View } from "react-native";

import { AddItemModal } from "~/features/items/AddItemModal";
import { categoryLabel, statusLabel } from "~/features/items/labels";
import {
  AppText,
  Badge,
  Button,
  ButtonText,
  Card,
  EmptyState,
  ListRow,
  MetricCard,
  ProtectedScreen,
  SectionHeader,
  Stack,
} from "~/components/ui";
import { usePlan } from "~/hooks/usePlan";
import { useKits } from "~/state/kits";
import { useItems, useUpcomingReminders } from "~/state/items";
import { spacing } from "~/styles/theme";

export default function DashboardScreen() {
  const router = useRouter();
  const plan = usePlan();
  const kits = useKits();
  const items = useItems();
  const reminders = useUpcomingReminders(5);
  const [addOpen, setAddOpen] = useState(false);

  const recent = useMemo(
    () =>
      [...items]
        .sort((a, b) => b.updated_at.localeCompare(a.updated_at))
        .slice(0, 6),
    [items],
  );

  return (
    <ProtectedScreen
      title="Workshop"
      subtitle="Kits, serials, receipts, and service dates — all in one place."
    >
      <View style={stylesheet.metrics}>
        <View style={stylesheet.metric}>
          <MetricCard
            label="Items"
            value={
              plan.items.limit === null
                ? String(items.length)
                : `${items.length} / ${plan.items.limit}`
            }
          />
        </View>
        <View style={stylesheet.metric}>
          <MetricCard label="Kits" value={String(kits.length)} />
        </View>
        <View style={stylesheet.metric}>
          <MetricCard
            label={plan.isPro ? "Reminders" : "Free remaining"}
            value={
              plan.isPro
                ? String(reminders.length)
                : String(plan.items.remaining ?? 0)
            }
          />
        </View>
      </View>

      <Card gap="md">
        <SectionHeader
          title="Kits"
          description="Group gear by bag, van, bench, or job."
        />
        {kits.length === 0 ? (
          <EmptyState
            title="No kits yet"
            description="Create a kit from the Kits tab, or add items ungrouped."
          />
        ) : (
          <Stack gap="none">
            {kits.slice(0, 4).map((kit, index) => {
              const count = items.filter(
                (item) => item.kit_id === kit.id,
              ).length;
              return (
                <ListRow
                  key={kit.id}
                  first={index === 0}
                  title={kit.name}
                  description={kit.notes ?? undefined}
                  onPress={() => router.push(`/kits/${kit.id}` as never)}
                  meta={
                    <Badge tone="neutral">
                      {count} {count === 1 ? "item" : "items"}
                    </Badge>
                  }
                />
              );
            })}
          </Stack>
        )}
        <Button
          align="start"
          variant="outline"
          onPress={() => router.push("/kits" as never)}
        >
          <ButtonText>All kits</ButtonText>
        </Button>
      </Card>

      <Card gap="md">
        <SectionHeader
          title="Recent items"
          description="The last things you added or edited."
        />
        {recent.length === 0 ? (
          <EmptyState
            title="No gear yet"
            description="Add a drill, camera, laptop, or whatever you need to keep track of."
          />
        ) : (
          <Stack gap="none">
            {recent.map((item, index) => (
              <ListRow
                key={item.id}
                first={index === 0}
                title={item.name}
                description={[item.brand, item.model].filter(Boolean).join(" ")}
                onPress={() => router.push(`/items/${item.id}` as never)}
                meta={
                  <>
                    <Badge tone="neutral">{categoryLabel(item.category)}</Badge>
                    <Badge tone="neutral">{statusLabel(item.status)}</Badge>
                  </>
                }
              />
            ))}
          </Stack>
        )}
        {plan.items.allowed ? (
          <Button
            align="start"
            iconName="plus"
            onPress={() => setAddOpen(true)}
          >
            <ButtonText>Add item</ButtonText>
          </Button>
        ) : (
          <AppText size="xs">{plan.items.message}</AppText>
        )}
      </Card>

      {plan.isPro && reminders.length > 0 ? (
        <Card gap="md">
          <SectionHeader title="Coming due" />
          <Stack gap="none">
            {reminders.map((reminder, index) => (
              <ListRow
                key={reminder.id}
                first={index === 0}
                title={reminder.itemName}
                description={`${reminder.kind} · ${reminder.due_on}`}
                onPress={() =>
                  router.push(`/items/${reminder.item_id}` as never)
                }
                meta={
                  reminder.overdue ? (
                    <Badge tone="danger">Overdue</Badge>
                  ) : (
                    <Badge tone="neutral">Due</Badge>
                  )
                }
              />
            ))}
          </Stack>
        </Card>
      ) : null}

      <AddItemModal
        visible={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={(itemId) => {
          setAddOpen(false);
          router.push(`/items/${itemId}` as never);
        }}
      />
    </ProtectedScreen>
  );
}

const stylesheet = StyleSheet.create({
  metrics: { flexDirection: "row", flexWrap: "wrap", gap: spacing.m },
  metric: { minWidth: 160, flex: 1 },
});
